import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { partnerReservationsRepo } from "@/data/repos/partnerReservationsRepo";
import type { ReservationLookup } from "@/domain/reservation/types";

export function reservationLookupQueryKey(code: string) {
  return ["reservation-lookup", code] as const;
}

export function useReservationLookup(code: string) {
  const queryClient = useQueryClient();
  const queryKey = reservationLookupQueryKey(code);

  const lookup = useQuery({
    queryKey,
    queryFn: () => partnerReservationsRepo.lookup(code),
    enabled: code !== "",
    // An unknown code is an answer, not a blip: retrying it would keep a
    // partner waiting at a door for a result that will not change.
    retry: false,
    staleTime: 0,
  });

  const recordArrival = useMutation({
    mutationFn: (reservationId: number) => partnerReservationsRepo.recordArrival(reservationId),
    // The arrival belongs to the code that was submitted, not to whatever code
    // the partner has on screen by the time the answer comes back.
    onMutate: () => ({ recordedFor: queryKey }),
    onSuccess: (arrival, _reservationId, context) => {
      queryClient.setQueryData<ReservationLookup>(context.recordedFor, (reservation) =>
        reservation ? { ...reservation, validatedAt: arrival.validatedAt } : reservation,
      );
    },
  });

  // A retry has to look like it is running, and the previous answer — an error
  // included — is no longer what the partner is waiting on.
  const isSearching = code !== "" && lookup.isFetching;

  return {
    reservation: code === "" ? undefined : lookup.data,
    isLoading: isSearching,
    error: isSearching ? null : lookup.error,
    searchAgain: lookup.refetch,
    recordArrival,
  };
}
